// Advertisement Management Component for Super Admin
// Includes creation, review, approval, and rejection of ads
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Plus, Check, X, Pause, Play, Trash2, Eye, AlertTriangle, 
  Shield, Clock, BarChart3, ExternalLink, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdManagement = ({ token }) => {
  const [ads, setAds] = useState([]);
  const [pendingAds, setPendingAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  
  const [newAd, setNewAd] = useState({
    title: '',
    description: '',
    category: '',
    image_url: '',
    link_url: '',
    advertiser_name: '',
    advertiser_contact: '',
    start_date: '',
    end_date: '',
    ethical_declaration: false
  });

  const [reviewData, setReviewData] = useState({
    decision: '',
    review_notes: '',
    ethical_concerns: []
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [adsRes, pendingRes, statsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/ads?include_all=true`, { headers }),
        axios.get(`${API_URL}/api/ads/pending`, { headers }),
        axios.get(`${API_URL}/api/ads/stats`, { headers }),
        axios.get(`${API_URL}/api/ads/categories`)
      ]);
      
      setAds(adsRes.data.ads || []);
      setPendingAds(pendingRes.data.ads || []);
      setStats(statsRes.data);
      setCategories(categoriesRes.data.categories || []);
      setGuidelines(categoriesRes.data.ethical_guidelines);
    } catch (error) {
      toast.error('Erreur de chargement des publicités');
    }
    setLoading(false);
  };

  const handleCreateAd = async () => {
    if (!newAd.ethical_declaration) {
      toast.error('Vous devez certifier le respect des règles éthiques');
      return;
    }
    
    if (!newAd.title || !newAd.description || !newAd.category || !newAd.advertiser_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/ads`, newAd, { headers });
      toast.success('Publicité créée et en attente d\'approbation');
      setShowCreateModal(false);
      setNewAd({
        title: '',
        description: '',
        category: '',
        image_url: '',
        link_url: '',
        advertiser_name: '',
        advertiser_contact: '',
        start_date: '',
        end_date: '',
        ethical_declaration: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de création');
    }
  };

  const handleReviewAd = async () => {
    if (!reviewData.decision) {
      toast.error('Veuillez sélectionner une décision');
      return;
    }

    try {
      await axios.patch(`${API_URL}/api/ads/${selectedAd.id}/review`, reviewData, { headers });
      toast.success(reviewData.decision === 'approved' ? 'Publicité approuvée ✅' : 'Publicité rejetée ❌');
      setShowReviewModal(false);
      setSelectedAd(null);
      setReviewData({ decision: '', review_notes: '', ethical_concerns: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de validation');
    }
  };

  const handlePauseResume = async (ad) => {
    try {
      if (ad.status === 'approved') {
        await axios.patch(`${API_URL}/api/ads/${ad.id}/pause`, {}, { headers });
        toast.success('Publicité mise en pause');
      } else if (ad.status === 'paused') {
        await axios.patch(`${API_URL}/api/ads/${ad.id}/resume`, {}, { headers });
        toast.success('Publicité réactivée');
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Supprimer cette publicité ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/ads/${adId}`, { headers });
      toast.success('Publicité supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const openReviewModal = (ad) => {
    setSelectedAd(ad);
    setReviewData({ decision: '', review_notes: '', ethical_concerns: [] });
    setShowReviewModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
      paused: 'bg-slate-500/20 text-slate-400',
      expired: 'bg-gray-500/20 text-gray-400'
    };
    const labels = {
      pending: '⏳ En attente',
      approved: '✅ Approuvée',
      rejected: '❌ Rejetée',
      paused: '⏸️ En pause',
      expired: '⌛ Expirée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || badges.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-400">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-green-400" />
            Gestion des Publicités Éthiques
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Toutes les publicités sont soumises à validation éthique
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowGuidelinesModal(true)}
            className="border-amber-500/50 text-amber-400"
          >
            <FileText className="w-4 h-4 mr-2" />
            Règles Éthiques
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Publicité
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
              <p className="text-xs text-yellow-300">En attente</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{stats.approved}</p>
              <p className="text-xs text-green-300">Approuvées</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-400">{stats.rejected}</p>
              <p className="text-xs text-red-300">Rejetées</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-500/10 border-slate-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-400">{stats.paused}</p>
              <p className="text-xs text-slate-300">En pause</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{stats.total}</p>
              <p className="text-xs text-blue-300">Total</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        {[
          { id: 'pending', label: `En attente (${pendingAds.length})`, icon: Clock },
          { id: 'approved', label: 'Approuvées', icon: Check },
          { id: 'all', label: 'Toutes', icon: BarChart3 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === tab.id 
                ? 'bg-slate-700 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pending Ads Alert */}
      {activeTab === 'pending' && pendingAds.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-yellow-400" />
          <div>
            <p className="text-yellow-400 font-medium">
              {pendingAds.length} publicité(s) en attente de validation
            </p>
            <p className="text-yellow-300/70 text-sm">
              Vérifiez le respect des règles éthiques, morales et de pudeur avant approbation
            </p>
          </div>
        </div>
      )}

      {/* Ads List */}
      <div className="space-y-4">
        {(activeTab === 'pending' ? pendingAds : 
          activeTab === 'approved' ? ads.filter(a => a.status === 'approved') : 
          ads
        ).map(ad => (
          <Card key={ad.id} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {ad.image_url && (
                  <img 
                    src={ad.image_url} 
                    alt={ad.title}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                )}
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{categories.find(c => c.id === ad.category)?.icon || '📢'}</span>
                    <h3 className="text-white font-semibold">{ad.title}</h3>
                    {getStatusBadge(ad.status)}
                  </div>
                  
                  <p className="text-slate-400 text-sm mb-2">{ad.description}</p>
                  
                  <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                    <span>Annonceur: {ad.advertiser_name}</span>
                    <span>Catégorie: {ad.category_name}</span>
                    <span>Créé: {new Date(ad.created_at).toLocaleDateString('fr')}</span>
                    {ad.impressions > 0 && <span>👁 {ad.impressions} vues</span>}
                    {ad.clicks > 0 && <span>🖱 {ad.clicks} clics</span>}
                  </div>
                  
                  {ad.review_notes && (
                    <div className="mt-2 p-2 bg-slate-700/50 rounded text-xs text-slate-400">
                      <strong>Note de validation:</strong> {ad.review_notes}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {ad.status === 'pending' && (
                    <Button 
                      size="sm" 
                      onClick={() => openReviewModal(ad)}
                      className="bg-yellow-600 hover:bg-yellow-700"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Valider
                    </Button>
                  )}
                  
                  {(ad.status === 'approved' || ad.status === 'paused') && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handlePauseResume(ad)}
                      className={ad.status === 'approved' ? 'border-orange-500 text-orange-400' : 'border-green-500 text-green-400'}
                    >
                      {ad.status === 'approved' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                  )}
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDelete(ad.id)}
                    className="border-red-500 text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {((activeTab === 'pending' && pendingAds.length === 0) ||
          (activeTab === 'approved' && ads.filter(a => a.status === 'approved').length === 0) ||
          (activeTab === 'all' && ads.length === 0)) && (
          <div className="text-center py-12 text-slate-500">
            Aucune publicité dans cette catégorie
          </div>
        )}
      </div>

      {/* Create Ad Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nouvelle Publicité
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Créez une publicité qui sera soumise à validation éthique
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-slate-400 text-sm">Titre *</label>
              <Input
                value={newAd.title}
                onChange={(e) => setNewAd({...newAd, title: e.target.value})}
                placeholder="Titre de la publicité"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div>
              <label className="text-slate-400 text-sm">Description *</label>
              <textarea
                value={newAd.description}
                onChange={(e) => setNewAd({...newAd, description: e.target.value})}
                placeholder="Description détaillée"
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 min-h-[80px]"
              />
            </div>

            <div>
              <label className="text-slate-400 text-sm">Catégorie *</label>
              <Select value={newAd.category} onValueChange={(v) => setNewAd({...newAd, category: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Choisir une catégorie" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id} className="text-white">
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-sm">Nom de l'annonceur *</label>
                <Input
                  value={newAd.advertiser_name}
                  onChange={(e) => setNewAd({...newAd, advertiser_name: e.target.value})}
                  placeholder="Nom de l'entreprise"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm">Contact annonceur</label>
                <Input
                  value={newAd.advertiser_contact}
                  onChange={(e) => setNewAd({...newAd, advertiser_contact: e.target.value})}
                  placeholder="Email ou téléphone"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            <div>
              <label className="text-slate-400 text-sm">URL de l'image (optionnel)</label>
              <Input
                value={newAd.image_url}
                onChange={(e) => setNewAd({...newAd, image_url: e.target.value})}
                placeholder="https://..."
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div>
              <label className="text-slate-400 text-sm">Lien externe (optionnel)</label>
              <Input
                value={newAd.link_url}
                onChange={(e) => setNewAd({...newAd, link_url: e.target.value})}
                placeholder="https://..."
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-sm">Date de début</label>
                <Input
                  type="date"
                  value={newAd.start_date}
                  onChange={(e) => setNewAd({...newAd, start_date: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm">Date de fin</label>
                <Input
                  type="date"
                  value={newAd.end_date}
                  onChange={(e) => setNewAd({...newAd, end_date: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            {/* Ethical Declaration */}
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newAd.ethical_declaration}
                  onChange={(e) => setNewAd({...newAd, ethical_declaration: e.target.checked})}
                  className="mt-1 w-5 h-5 rounded border-green-500 bg-slate-600 text-green-500"
                />
                <div>
                  <p className="text-green-400 font-medium text-sm">
                    Déclaration de conformité éthique *
                  </p>
                  <p className="text-green-300/70 text-xs mt-1">
                    Je certifie que cette publicité respecte :
                  </p>
                  <ul className="text-green-300/70 text-xs mt-1 list-disc list-inside">
                    <li>L'éthique médicale et les réglementations sanitaires</li>
                    <li>La moralité et les valeurs familiales</li>
                    <li>La pudeur et la décence (pas de contenus suggestifs)</li>
                    <li>L'absence de contenus trompeurs ou discriminatoires</li>
                  </ul>
                </div>
              </label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => setShowCreateModal(false)} variant="outline" className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleCreateAd} className="flex-1 bg-green-600 hover:bg-green-700">
                Soumettre à validation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Validation Éthique
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Vérifiez le respect des règles éthiques, morales et de pudeur
            </DialogDescription>
          </DialogHeader>

          {selectedAd && (
            <div className="space-y-4 mt-4">
              {/* Ad Preview */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-start gap-4">
                  {selectedAd.image_url && (
                    <img 
                      src={selectedAd.image_url} 
                      alt={selectedAd.title}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  )}
                  <div>
                    <h3 className="text-white font-semibold">{selectedAd.title}</h3>
                    <p className="text-slate-400 text-sm mt-1">{selectedAd.description}</p>
                    <p className="text-slate-500 text-xs mt-2">
                      Annonceur: {selectedAd.advertiser_name} | 
                      Catégorie: {selectedAd.category_name}
                    </p>
                    {selectedAd.link_url && (
                      <a 
                        href={selectedAd.link_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-400 text-xs flex items-center gap-1 mt-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Voir le lien
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Ethical Checklist */}
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 font-medium text-sm mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Points à vérifier
                </p>
                <div className="space-y-2">
                  {guidelines?.forbidden_content?.slice(0, 6).map((item, idx) => (
                    <label key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                      <input 
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setReviewData({
                              ...reviewData, 
                              ethical_concerns: [...reviewData.ethical_concerns, item]
                            });
                          } else {
                            setReviewData({
                              ...reviewData,
                              ethical_concerns: reviewData.ethical_concerns.filter(c => c !== item)
                            });
                          }
                        }}
                        className="w-4 h-4 rounded border-red-500"
                      />
                      <span className="text-red-400">⚠️ {item}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-amber-500/30">
                  <p className="text-amber-300 text-xs mb-2">Règles de pudeur :</p>
                  {guidelines?.modesty_rules?.map((item, idx) => (
                    <label key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                      <input 
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setReviewData({
                              ...reviewData, 
                              ethical_concerns: [...reviewData.ethical_concerns, item]
                            });
                          } else {
                            setReviewData({
                              ...reviewData,
                              ethical_concerns: reviewData.ethical_concerns.filter(c => c !== item)
                            });
                          }
                        }}
                        className="w-4 h-4 rounded border-red-500"
                      />
                      <span className="text-red-400">⚠️ Non respecté: {item}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Review Notes */}
              <div>
                <label className="text-slate-400 text-sm">Notes de validation</label>
                <textarea
                  value={reviewData.review_notes}
                  onChange={(e) => setReviewData({...reviewData, review_notes: e.target.value})}
                  placeholder="Ajoutez des commentaires (optionnel)"
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 min-h-[80px]"
                />
              </div>

              {/* Decision Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={() => {
                    setReviewData({...reviewData, decision: 'rejected'});
                    setTimeout(handleReviewAd, 100);
                  }}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  <X className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button 
                  onClick={() => {
                    if (reviewData.ethical_concerns.length > 0) {
                      toast.error('Des problèmes éthiques ont été identifiés. Veuillez rejeter ou décocher les cases.');
                      return;
                    }
                    setReviewData({...reviewData, decision: 'approved'});
                    setTimeout(handleReviewAd, 100);
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approuver
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ethics Guidelines Modal */}
      <Dialog open={showGuidelinesModal} onOpenChange={setShowGuidelinesModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Règles Éthiques drbooking
            </DialogTitle>
          </DialogHeader>

          {guidelines && (
            <div className="space-y-6 mt-4">
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <h3 className="text-red-400 font-medium mb-3">❌ Contenus Interdits</h3>
                <ul className="space-y-1 text-sm text-slate-300">
                  {guidelines.forbidden_content?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <h3 className="text-green-400 font-medium mb-3">✅ Éléments Requis</h3>
                <ul className="space-y-1 text-sm text-slate-300">
                  {guidelines.required_elements?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-green-400">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <h3 className="text-purple-400 font-medium mb-3">🌸 Règles de Pudeur</h3>
                <ul className="space-y-1 text-sm text-slate-300">
                  {guidelines.modesty_rules?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-purple-400">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdManagement;
